<template>
    <div>
        <base-dialog :loading="loading" :value="dialog" :width="440" :title="$t('common.load_data')" @input="onInput" @close="hide">
            <skeleton-loader v-if="loading" type="rows" />
            <template v-else>
                <div class="load-field">
                    <label class="load-field-label">{{ $t('common.date') }}</label>
                    <div class="load-field-control">
                        <kimsa-date-picker solo flat dense color="secondary" :value="date" centered-value elevation
                            @change="onChangeDate" />
                    </div>
                </div>

                <div v-for="(item, i) in config" :key="item.id" class="load-field">
                    <label class="load-field-label">
                        {{ config[i].name }}
                        <span class="load-field-unit">{{ $t(`units.${config[i].unit}`) }}</span>
                    </label>
                    <div class="load-field-control">
                        <kimsa-text-field dense centered-value :placeholder="$t(`units.${config[i].unit}`)"
                            :value="payload[config[i].id]" @change="onChangeValue(i, $event)" @keypress="valueValidation"
                            custom-keypress force-value />
                    </div>
                </div>
            </template>

            <template #footer>
                <dialog-actions :confirm-label="$t('common.save')" :disabled="disabled" :loading="loadingSave"
                    @cancel="hide" @confirm="assign" />
            </template>
        </base-dialog>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { db } from "@/utils/Firebase";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import KimsaDatePicker from '../common/inputs/KimsaDatePicker.vue';
import BaseDialog from '@/components/common/BaseDialog';
import DialogActions from '@/components/common/DialogActions';
import SkeletonLoader from '@/components/common/SkeletonLoader.vue';

export default {
    name: "LoadDataEvaluationDialog",
    components: { KimsaTextField, KimsaDatePicker, BaseDialog, DialogActions, SkeletonLoader },
    props: {
        coachId: {
            type: String,
            default: null
        },
    },
    data() {
        return {
            dialog: false,
            loading: false,
            loadingSave: false,
            studentId: null,
            evaluationId: null,
            assignedEvaluationId: null,
            payload: {},
            hasLoadedToday: false,
            currentAE: null,
            date: this.$moment().format('YYYY-MM-DD'),
        }
    },
    computed: {
        ...mapState(['evaluations']),
        disabled(vm = this) {
            return vm.loading || !vm.hasValues
        },
        hasValues(vm = this) {
            return vm.config.some(c => {
                const v = vm.payload?.[c.id]
                return v !== null && v !== undefined && v !== '' && !isNaN(v)
            })
        },
        currentEvaluation(vm = this) {
            if (!vm.evaluationId) return null
            return vm.evaluations.find(eva => eva.id === vm.evaluationId) || null
        },
        config(vm = this) {
            if (!vm.currentEvaluation) return []
            return vm.currentEvaluation.config || []
        },
        formattedDate(vm = this) {
            return vm.$moment(vm.date).format('DD-MM-YYYY')
        },
    },
    async mounted() {
        const vm = this
        vm.loadEvaluations()
    },
    methods: {
        ...mapActions(['bindEvaluations']),
        show(studentId, assignedEvaluationId) {
            const vm = this
            vm.studentId = studentId
            vm.assignedEvaluationId = assignedEvaluationId
            vm.dialog = true
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.reset()
        },
        onInput(val) {
            if (!val) this.hide()
        },
        reset() {
            const vm = this
            vm.studentId = null
            vm.evaluationId = null
            vm.assignedEvaluationId = null
            vm.hasLoadedToday = false
            vm.currentAE = null
            vm.date = vm.$moment().format('YYYY-MM-DD')
        },
        async loadEvaluations() {
            const vm = this;
            if (vm._checkNeedReloadBind(vm.evaluations, vm.coachId)) await vm.bindEvaluations(vm.coachId)
        },
        async loadAssignedEvaluation() {
            const vm = this
            await vm.withLoading(async () => {
                let ref = await db.collection('assignedEvaluations').doc(vm.assignedEvaluationId).get()
                if (!ref.exists) {
                    vm.toast(vm.$t('common.an_error_has_occurred'), 'error')
                    return vm.hide()
                }
                vm.currentAE = ref.data()

                let values = vm.currentAE?.values?.find(value => value.date === vm.formattedDate)
                if (values) {
                    vm.payload = values
                    vm.hasLoadedToday = true
                }

                vm.evaluationId = vm.currentAE.evaluationId || null
            })
        },
        onChangeDate(date) {
            const vm = this
            if (!vm.dialog) return;
            vm.date = date
            let values = vm.currentAE?.values?.find(value => value.date === vm.formattedDate)
            if (values) {
                vm.payload = values
                vm.hasLoadedToday = true
            } else {
                vm.payload = { date: vm.formattedDate }
                const config = vm.currentEvaluation?.config || []
                config.forEach(con => {
                    vm.$set(vm.payload, con.id, null)
                })
                vm.hasLoadedToday = false
            }
        },
        async assign() {
            const vm = this
            await vm.withLoading(async () => {
                let payloadValues = { values: vm.currentAE.values || [] }
                const index = vm.currentAE?.values?.findIndex(value => value.date === vm.formattedDate)
                if (index !== -1) {
                    payloadValues.values[index] = vm.payload
                } else {
                    payloadValues.values.push(vm.payload)
                }

                payloadValues.values.sort((a, b) => vm.parseDateString(a.date) - vm.parseDateString(b.date))
                await db.collection('assignedEvaluations').doc(vm.assignedEvaluationId).update(payloadValues)

                vm.toast(vm.$t('common.loaded_data'))
                vm.hide()
            }, 'loadingSave')
        },
        onChangeValue(i, value) {
            const vm = this
            const parsed = parseFloat((value ?? '').toString().replace(/,/g, '.'))
            vm.$set(vm.payload, vm.config[i].id, isNaN(parsed) ? null : parsed)
        },
        valueValidation(event) {
            const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '-', ',', '.']
            if (!allowedKeys.includes(event.key) && event.key !== 'Backspace') event.preventDefault()
        },
        parseDateString(dateString) {
            const [day, month, year] = dateString.split('-').map(Number)
            return new Date(year, month - 1, day)
        }
    },
    watch: {
        assignedEvaluationId(val) {
            if (val) this.loadAssignedEvaluation()
        },
        currentEvaluation(val) {
            const vm = this
            if (!val || vm.hasLoadedToday) return;
            vm.payload = { date: vm.formattedDate }
            val.config.forEach(con => {
                vm.$set(vm.payload, con.id, null)
            })
        },
        coachId() {
            this.loadEvaluations()
        },
    }
}
</script>

<style scoped>
.load-field {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 0;
    border-bottom: 1px solid var(--v-outline-base);
}

.load-field:last-child {
    border-bottom: none;
}

.load-field-label {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    font-weight: 500;
    color: var(--v-txtPrimary-base);
}

.load-field-unit {
    color: var(--v-txtMuted-base);
    font-size: 11px;
    margin-left: 4px;
}

.load-field-control {
    width: 150px;
    flex-shrink: 0;
}
</style>
